/**
 * Certificates Redux Slice
 */

import { createSlice, type PayloadAction } from '@spcc/core'
import type { Certificate, CreateCertificateRequest, UpdateCertificateRequest } from '../infrastructure/api/certificates-api'

export interface CertificatesFilter {
  type: 'all' | 'general' | 'variant'
  isActive: 'all' | 'true' | 'false'
  search: string
}

export interface CertificatesState {
  items: Certificate[]
  selected: Certificate | null
  drawerOpen: boolean
  drawerMode: 'create' | 'edit'
  filter: CertificatesFilter
  loading: boolean
  saving: boolean
  error: string | null
}

const initialState: CertificatesState = {
  items: [],
  selected: null,
  drawerOpen: false,
  drawerMode: 'create',
  filter: { type: 'all', isActive: 'all', search: '' },
  loading: false,
  saving: false,
  error: null,
}

const certificatesSlice = createSlice({
  name: 'certificates',
  initialState,
  reducers: {
    // Fetch
    fetchCertificates: (state, _action: PayloadAction<{ type?: 'all' | 'general' | 'variant' } | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchCertificatesSuccess: (state, action: PayloadAction<Certificate[]>) => {
      state.items = action.payload
      state.loading = false
    },
    fetchCertificatesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    // Create
    createCertificate: (state, _action: PayloadAction<CreateCertificateRequest>) => {
      state.saving = true
      state.error = null
    },
    createCertificateSuccess: (state, action: PayloadAction<Certificate>) => {
      state.items = [action.payload, ...state.items]
      state.saving = false
      state.drawerOpen = false
    },
    createCertificateFailure: (state, action: PayloadAction<string>) => {
      state.saving = false
      state.error = action.payload
    },

    // Update
    updateCertificate: (state, _action: PayloadAction<{ documentId: string; data: UpdateCertificateRequest }>) => {
      state.saving = true
      state.error = null
    },
    updateCertificateSuccess: (state, action: PayloadAction<Certificate>) => {
      state.items = state.items.map((c) =>
        c.documentId === action.payload.documentId ? action.payload : c
      )
      state.saving = false
      state.drawerOpen = false
    },
    updateCertificateFailure: (state, action: PayloadAction<string>) => {
      state.saving = false
      state.error = action.payload
    },

    // Delete
    deleteCertificate: (state, _action: PayloadAction<string>) => {
      state.saving = true
      state.error = null
    },
    deleteCertificateSuccess: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((c) => c.documentId !== action.payload)
      state.saving = false
    },
    deleteCertificateFailure: (state, action: PayloadAction<string>) => {
      state.saving = false
      state.error = action.payload
    },

    // UI
    openCreateDrawer: (state) => {
      state.selected = null
      state.drawerMode = 'create'
      state.drawerOpen = true
      state.error = null
    },
    openEditDrawer: (state, action: PayloadAction<Certificate>) => {
      state.selected = action.payload
      state.drawerMode = 'edit'
      state.drawerOpen = true
      state.error = null
    },
    closeDrawer: (state) => {
      state.drawerOpen = false
      state.selected = null
      state.error = null
    },
    setFilter: (state, action: PayloadAction<Partial<CertificatesFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },
  },
})

export const { actions: certificatesActions } = certificatesSlice
export const certificatesReducer = certificatesSlice.reducer
